// /rutinas — vista cross-pacientes del kine de las rutinas activas
// prescriptas a sus pacientes hoy. Wireada con `usePlanesActivosKine`
// (consume `planes` + `plan_ejercicios` filtrando por kine_id via
// pacientes.kine_id, ver hook para FK chain).
//
// Naming UX: "rutina" en pantalla = `plan` + `plan_ejercicios` en DB.
// Decisión documentada en docs/decisions/rutinas-prescription-flow.md
// (Camino C). Templates reutilizables (rutinas + rutina_ejercicios) están
// deferidos a Sprint 3+.

import { useState } from 'react';
import { Link } from 'react-router';
import { Search, Plus, LayoutDashboard, Users } from 'lucide-react';
import { Avatar } from '../components/Avatar';
import { KineHeader } from '../components/kine/KineHeader';
import { KineSidebar } from '../components/kine/KineSidebar';
import { useKineProfile } from '@/lib/supabase/queries/useKineProfile';
import { useCurrentKineId } from '@/app/components/auth/AuthProvider';
import {
  usePlanesActivosKine,
  type PlanActivoKine,
} from '@/lib/supabase/queries/usePlanesActivosKine';
import { formatRelativeDate } from '@/lib/utils';

export function RutinasKine() {
  const kineId = useCurrentKineId();
  const { data: planes, error, loading } = usePlanesActivosKine(kineId);
  const { data: kineProfile } = useKineProfile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [busqueda, setBusqueda] = useState('');

  const q = busqueda.trim().toLowerCase();
  const planesFiltrados = (planes ?? []).filter(
    (p) =>
      !q ||
      p.nombre.toLowerCase().includes(q) ||
      `${p.paciente.nombre} ${p.paciente.apellido}`.toLowerCase().includes(q),
  );

  const pacientesActivos = planes
    ? new Set(planes.map((p) => p.paciente.id)).size
    : 0;
  const headerSubtitle = loading
    ? 'Cargando…'
    : planes && planes.length > 0
      ? `${planes.length} ${planes.length === 1 ? 'rutina activa' : 'rutinas activas'} en ${pacientesActivos} ${pacientesActivos === 1 ? 'paciente' : 'pacientes'}`
      : 'Sin rutinas activas';

  return (
    <div className="min-h-screen bg-background transition-colors duration-200">
      <KineHeader
        kineNombre={kineProfile?.nombre || '—'}
        onMenuClick={() => setDrawerOpen(true)}
      />

      <div className="flex">
        <KineSidebar
          activeKey="rutinas"
          mobileOpen={drawerOpen}
          onMobileClose={() => setDrawerOpen(false)}
        />

        <div className="flex-1 p-6">
          <div className="max-w-[1200px]">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl">Rutinas</h1>
                <p className="text-muted-foreground mt-1.5 text-sm">{headerSubtitle}</p>
              </div>
              {/* La prescripción se hace desde DetallePaciente del paciente
                  target (Camino C). Desde Rutinas el kine empieza eligiendo
                  paciente → linkeamos al Dashboard de pacientes. */}
              <Link
                to="/"
                className="bg-primary text-primary-foreground px-5 py-2.5 rounded-full flex items-center gap-2 text-sm font-semibold shadow-sm transition-all duration-200 ease-out hover:shadow-md"
                title="Elegí un paciente para prescribirle una rutina nueva"
              >
                <Plus className="w-4 h-4" strokeWidth={2} />
                Prescribir rutina
              </Link>
            </div>

            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                <input
                  type="text"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Buscar por rutina o paciente..."
                  className="w-full pl-11 pr-4 py-2.5 text-sm bg-input-background border border-border rounded-full transition-all duration-200 ease-out focus:ring-2 focus:ring-ring focus:border-transparent"
                />
              </div>
            </div>

            {loading && <PlanesGridSkeleton />}
            {error && !loading && <PlanesGridError message={error} />}
            {!loading && !error && planes && planes.length === 0 && (
              <PlanesGridEmpty />
            )}
            {!loading && !error && planes && planes.length > 0 && (
              <PlanesGrid planes={planesFiltrados} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Subcomponents ────────────────────────────────────────────────────────

function PlanesGrid({ planes }: { planes: PlanActivoKine[] }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {planes.map((plan, idx) => (
        <PlanCard key={plan.id} plan={plan} colorIndex={idx} />
      ))}
    </div>
  );
}

function PlanCard({ plan, colorIndex }: { plan: PlanActivoKine; colorIndex: number }) {
  const pacienteName = `${plan.paciente.nombre} ${plan.paciente.apellido}`;
  const frecuencia = deriveFrecuencia(plan.ejercicios);
  const desde = formatRelativeDate(new Date(plan.fecha_inicio));

  return (
    <div className="bg-card rounded-xl p-5 border border-border shadow-sm transition-all duration-200 ease-out hover:shadow-md hover:border-primary/30">
      <div className="mb-3">
        <h3 className="font-semibold text-base mb-2">{plan.nombre}</h3>
        <Link
          to={`/paciente/${plan.paciente.id}`}
          className="flex items-center gap-2 hover:text-primary transition-colors"
        >
          <Avatar name={pacienteName} colorIndex={colorIndex} />
          <span className="text-sm font-medium">{pacienteName}</span>
        </Link>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Ejercicios</span>
          <span className="font-semibold tabular-nums">{plan.ejercicios.length}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Frecuencia</span>
          <span className="font-semibold">{frecuencia}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Desde</span>
          <span className="font-medium">{desde}</span>
        </div>
      </div>

      <Link
        to={`/paciente/${plan.paciente.id}`}
        className="block w-full px-3 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold text-center transition-colors hover:bg-primary/90"
      >
        Ver paciente
      </Link>
    </div>
  );
}

/** Deriva el label de frecuencia consolidando los `frecuencia_semanal` de
 *  los plan_ejercicios. Si todos coinciden, muestra "N días/semana". Si
 *  difieren entre ejercicios, "Variable". Si ninguno tiene valor, "—". */
function deriveFrecuencia(ejercicios: PlanActivoKine['ejercicios']): string {
  const valores = new Set(
    ejercicios
      .map((e) => e.frecuencia_semanal)
      .filter((v): v is number => v !== null),
  );
  if (valores.size === 0) return '—';
  if (valores.size === 1) {
    const v = Array.from(valores)[0];
    return `${v} ${v === 1 ? 'día/semana' : 'días/semana'}`;
  }
  return 'Variable';
}

function PlanesGridSkeleton() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {[0, 1].map((i) => (
        <div
          key={i}
          className="bg-card rounded-xl p-5 border border-border shadow-sm"
        >
          <div className="animate-pulse space-y-3">
            <div className="h-5 w-2/3 bg-muted rounded" />
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-muted" />
              <div className="h-4 w-32 bg-muted rounded" />
            </div>
            <div className="space-y-2 pt-2">
              <div className="h-4 w-full bg-muted rounded" />
              <div className="h-4 w-full bg-muted rounded" />
              <div className="h-4 w-full bg-muted rounded" />
            </div>
            <div className="h-9 w-full bg-muted rounded-lg" />
          </div>
        </div>
      ))}
    </div>
  );
}

function PlanesGridError({ message }: { message: string }) {
  return (
    <div className="bg-card rounded-xl p-6 border border-border text-center">
      <p className="font-semibold text-orange-600 mb-1">
        No pudimos cargar las rutinas
      </p>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

function PlanesGridEmpty() {
  return (
    <div className="bg-card rounded-xl p-8 border border-border text-center">
      <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mx-auto mb-4">
        <LayoutDashboard className="w-8 h-8 text-primary" strokeWidth={1.5} />
      </div>
      <h2 className="text-lg font-semibold mb-2">
        Aún no prescribiste rutinas a tus pacientes
      </h2>
      <p className="text-sm text-muted-foreground leading-relaxed mb-5 max-w-md mx-auto">
        Desde el detalle del paciente podés prescribir una rutina nueva con
        los ejercicios del catálogo.
      </p>
      <Link
        to="/"
        className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-full text-sm font-semibold transition-colors hover:bg-primary/90"
      >
        <Users className="w-4 h-4" strokeWidth={2} />
        Ir a mis pacientes
      </Link>
    </div>
  );
}
